/**
 * CSS Management Service
 * Handles dynamic CSS injection based on layout
 */

const layoutCssMap = {
  'MainSiteLayout': [
    '/main-site/css/bootstrap.min.css',
    '/main-site/css/bootstrap-theme.min.css',
    '/main-site/css/block_grid_bootstrap.css',
    '/main-site/css/font-awesome.min.css',
    '/main-site/css/typicons.min.css',
    '/main-site/css/odometer-theme-default.css',
    '/main-site/css/slider-pro.min.css',
    '/main-site/css/animate-custom.css',
    '/main-site/css/owl.carousel.css',
    '/main-site/css/owl.theme.default.min.css',
    '/main-site/css/slicknav.min.css',
    '/main-site/css/style.css'
  ],
  'LandpageLayout': [],
  'ScheduleLayout': [
    '/main-site/css/bootstrap.min.css',
    '/main-site/css/bootstrap-theme.min.css',
    '/main-site/css/style.css',
    '/style.css',
    'https://cdnjs.cloudflare.com/ajax/libs/bootstrap-datepicker/1.8.0/css/bootstrap-datepicker.css',
    'https://cdn.jsdelivr.net/npm/bootstrap-icons@1.8.0/font/bootstrap-icons.css'
  ]
  ,
  'HelpLayout': [
    'https://cdn.tailwindcss.com',
    '/main-site/css/help.css'
  ]
};

class CSSManager {
  constructor() {
    this.currentLayout = null;
    this.loadedCss = new Set();
  }

  /**
   * Injects CSS files for a given layout
   * Called SYNCHRONOUSLY before component renders to prevent FOUC
   */
  injectLayoutCss(layoutName) {
    const cssFiles = layoutCssMap[layoutName] || [];
    const loadPromises = [];
    console.debug(`CSSManager: injecting styles for layout ${layoutName}`, cssFiles);

    cssFiles.forEach(file => {
      // allow injecting either stylesheet links or scripts (e.g., Tailwind CDN)
      const isScript = file.endsWith('.js') || file.includes('tailwindcss.com');
      if (isScript) {
        if (!this.loadedCss.has(file) && !document.querySelector(`script[src="${file}"]`)) {
          const script = document.createElement('script');
          script.src = file;
          script.async = false;
          script.dataset.layout = 'dynamic';

          const p = new Promise(resolve => {
            script.onload = () => { this.loadedCss.add(file); resolve(); };
            script.onerror = () => { console.error(`Failed to load script: ${file}`); this.loadedCss.add(file); resolve(); };
          });

          document.head.appendChild(script);
          loadPromises.push(p);
        }
      } else {
        if (!this.loadedCss.has(file) && !document.querySelector(`link[href="${file}"]`)) {
          const link = document.createElement('link');
          link.rel = 'stylesheet';
          link.href = file;
          link.dataset.layout = 'dynamic';

          const p = new Promise(resolve => {
            // resolve on load or error (we still proceed even if a file fails)
            link.onload = () => {
              this.loadedCss.add(file);
              resolve();
            };
            link.onerror = () => {
              console.error(`Failed to load stylesheet: ${file}`);
              this.loadedCss.add(file);
              resolve();
            };
          });

          document.head.appendChild(link);
          loadPromises.push(p);
        }
      }
    });

    this.currentLayout = layoutName;

    if (loadPromises.length === 0) {
      return Promise.resolve();
    }

    // Safety timeout to avoid blocking navigation if a stylesheet hangs
    const timeoutMs = 2500;
    const timeoutPromise = new Promise(resolve => {
      setTimeout(() => {
        console.warn(`CSSManager: timeout after ${timeoutMs}ms while loading styles for ${layoutName}`);
        resolve();
      }, timeoutMs);
    });

    // Return a promise that resolves when all stylesheets have finished loading or when timeout occurs
    const allSettled = Promise.all(loadPromises);
    return Promise.race([allSettled, timeoutPromise]).then(() => {
      console.debug(`CSSManager: styles injected for layout ${layoutName}`);
    });
  }

  /**
   * Removes CSS for the current layout when navigating away
   */
  removeLayoutCss(layoutName) {
    const target = layoutName || this.currentLayout;
    const oldCssFiles = layoutCssMap[target] || [];

    oldCssFiles.forEach(file => {
      const link = document.querySelector(`link[href="${file}"]`);
      if (link) {
        link.remove();
        this.loadedCss.delete(file);
      }
    });

    if (this.currentLayout === target) this.currentLayout = null;
  }
}

export default new CSSManager();
